import { useCallback, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useCopilotStore } from "@/stores/use-copilot-store";
import { CoworkDrawer, type CopilotActivity } from "@/components/cowork/CoworkDrawer";
import { Button } from "./button";
import { cn } from "@/lib/utils";

/**
 * The Copilot launcher and its drawer.
 *
 * The drawer stays mounted while it is closed so the conversation survives a
 * close/open and so a reply that arrives with the drawer closed can be marked
 * on the launcher.
 */
export function AICopilotFloating({ hideLauncher = false }: { hideLauncher?: boolean }) {
  const { isOpen, openCopilot, closeCopilot } = useCopilotStore();
  const [activity, setActivity] = useState<CopilotActivity>({
    streaming: false,
    unread: false,
  });
  const reduceMotion = useReducedMotion();

  const handleActivityChange = useCallback((next: CopilotActivity) => {
    setActivity(next);
  }, []);

  const label = activity.streaming
    ? "Abrir o copiloto (respondendo)"
    : activity.unread
      ? "Abrir o copiloto (nova resposta)"
      : "Abrir o copiloto";

  return (
    <>
      <AnimatePresence>
        {!isOpen && !hideLauncher && (
          <motion.div
            key="copilot-launcher"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            // The launcher sits above whatever fixed bar is docked at the
            // bottom of the page; `--app-dock-height` is 0 when there is none.
            style={{ bottom: "calc(1.5rem + var(--app-dock-height, 0px) + env(safe-area-inset-bottom, 0px))" }}
            data-copilot-launcher
            data-testid="copilot-launcher"
            className="fixed right-6 z-40"
          >
            <Button
              type="button"
              variant="petrol"
              aria-label={label}
              onClick={() => openCopilot()}
              className="relative h-11 gap-2 rounded-full px-4 shadow-premium-sm hover:shadow-premium-md"
            >
              <Sparkles aria-hidden="true" />
              <span className="hidden md:inline">Copiloto</span>
              {(activity.streaming || activity.unread) && (
                <span
                  aria-hidden="true"
                  data-testid="copilot-launcher-indicator"
                  className={cn(
                    "absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-app-bg",
                    activity.streaming
                      ? "bg-app-info motion-safe:animate-pulse"
                      : "bg-app-success"
                  )}
                />
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      <CoworkDrawer
        open={isOpen}
        onClose={closeCopilot}
        onActivityChange={handleActivityChange}
      />
    </>
  );
}
